/**
 * 支出登録モーダルの カスタムフィールド 表示 + 追加 UI。
 *
 * Unit / ManagementTask と同じ UX:
 *  - グローバル定義 (ExpenseCustomFieldRecord) を expense_type で絞ってレンダー
 *  - 末尾に「+項目を追加」インライン入力で **pending fields** に push (API は叩かない)
 *  - pending fields は親 (Expenses/Detail) が保持し、record の登録/更新 submit 時に
 *    まとめて master upsert → custom_fields にマージする
 *
 * 入力 type は text 固定 (Unit / ManagementTask と同仕様)。
 */

import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomFormCreateInput } from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import type { AppDispatch, RootState } from "@/store";
import { fetchExpenseCustomFields } from "@/store/expenseCustomFieldSlice";
import type { ExpenseCustomFieldRecord } from "@/store/expenseCustomFieldSlice";
import type { ExpenseCustomFieldValue } from "@/store/expenseSlice";

export interface PendingExpenseCustomField {
  temp_id: string;
  label: string;
  value: string;
}

interface Props {
  /** この支出の経費区分 (グローバル定義の絞り込みに使用) */
  expenseType?: "OPEX" | "CAPEX";
  /** グローバル定義に対する値 */
  values: ExpenseCustomFieldValue[];
  onChange: (next: ExpenseCustomFieldValue[]) => void;
  /** 未保存の追加項目 (controlled) */
  pendingFields: PendingExpenseCustomField[];
  onPendingChange: (next: PendingExpenseCustomField[]) => void;
}

const ExpenseCustomFieldsAddable: React.FC<Props> = ({
  expenseType,
  values,
  onChange,
  pendingFields,
  onPendingChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: definitions, loading, loaded } = useSelector(
    (s: RootState) => s.expenseCustomFields.list,
  );

  const [pendingNewLabel, setPendingNewLabel] = useState("");

  useEffect(() => {
    if (!loaded && !loading) {
      void dispatch(fetchExpenseCustomFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当該経費区分に該当するグローバル定義
  const applicableGlobals = useMemo<ExpenseCustomFieldRecord[]>(
    () =>
      definitions
        .filter(
          (d) =>
            !d.applies_to_expense_types ||
            d.applies_to_expense_types.length === 0 ||
            !expenseType ||
            d.applies_to_expense_types.includes(expenseType),
        )
        .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)),
    [definitions, expenseType],
  );

  const valueMap = new Map(values.map((v) => [v.field_id, v.value]));

  const updateGlobalValue = (fieldId: string, v: any) => {
    const next = values.filter((x) => x.field_id !== fieldId);
    next.push({ field_id: fieldId, value: v });
    onChange(next);
  };

  // 「+項目を追加」 — API は叩かず pending に積むだけ。submit 時に親 (Expenses/Detail) が
  // master upsert → custom_fields にマージする。
  const handleAdd = () => {
    const label = pendingNewLabel.trim();
    if (!label) return;
    const newItem: PendingExpenseCustomField = {
      temp_id: `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      label,
      value: "",
    };
    onPendingChange([...pendingFields, newItem]);
    setPendingNewLabel("");
  };

  const updatePendingValue = (tempId: string, v: string) => {
    onPendingChange(
      pendingFields.map((p) =>
        p.temp_id === tempId ? { ...p, value: v } : p,
      ),
    );
  };

  const removePending = (tempId: string) => {
    onPendingChange(pendingFields.filter((p) => p.temp_id !== tempId));
  };

  return (
    <Box>
      {/* グローバル定義 (text/number/date/select/checkbox/textarea を一括描画) */}
      {applicableGlobals.length > 0 && (
        <Stack spacing={0}>
          {applicableGlobals.map((def) => (
            <GlobalFieldInput
              key={def.field_id}
              field={def}
              value={valueMap.get(def.field_id) ?? null}
              onChange={(v) => updateGlobalValue(def.field_id, v)}
            />
          ))}
        </Stack>
      )}

      {/* pending 項目 (未保存): submit 時に master upsert される */}
      {pendingFields.length > 0 && (
        <Stack spacing={0} sx={{ mt: applicableGlobals.length > 0 ? 0.5 : 0 }}>
          {pendingFields.map((p) => (
            <Box key={p.temp_id} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: "#3E3E3E", mb: 0.5 }}>
                {p.label}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 600, maxWidth: "100%" }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={p.value}
                    onChange={(e) =>
                      updatePendingValue(p.temp_id, e.target.value)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        height: "36px",
                        fontSize: 12,
                      },
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removePending(p.temp_id)}
                  title="この項目を削除"
                  sx={{ color: "#999" }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* 「+項目を追加」入力 */}
      <Box
        sx={{
          mt: applicableGlobals.length + pendingFields.length > 0 ? 2 : 0.5,
          maxWidth: "100%",
        }}
      >
        <CustomFormCreateInput
          value={pendingNewLabel}
          onChange={setPendingNewLabel}
          onAdd={handleAdd}
        />
      </Box>
    </Box>
  );
};

/** グローバル定義 1 つ分の入力 (type に応じて切替)。controlled */
const GlobalFieldInput: React.FC<{
  field: ExpenseCustomFieldRecord;
  value: any;
  onChange: (v: any) => void;
}> = ({ field, value, onChange }) => {
  // RHF の register interface を満たすための「mini form」として 1 field を作る
  const { register } = useForm<{ v: any }>({
    defaultValues: { v: value ?? "" },
  });

  if (field.field_type === "checkbox") {
    return (
      <Box sx={{ mb: 1, mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <Typography sx={{ fontSize: 12, color: "#3E3E3E" }}>
            {field.label}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // text / number / textarea / select / date は全部 TextField で描画
  // (高機能版を使いたい場合は今後 CustomTextInput / CustomSingleSelect 等に差し替え)
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 12, color: "#3E3E3E", mb: 0.5 }}>
        {field.label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        type={
          field.field_type === "number"
            ? "number"
            : field.field_type === "date"
              ? "date"
              : "text"
        }
        multiline={field.field_type === "textarea"}
        rows={field.field_type === "textarea" ? 3 : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        select={field.field_type === "select"}
        SelectProps={{ native: true }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            fontSize: 12,
          },
        }}
      >
        {field.field_type === "select" && (
          <>
            <option value="">選択してください</option>
            {(field.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </>
        )}
      </TextField>
      {/* RHF 静的登録 (formState に乗せたい場合に使う) */}
      <input type="hidden" {...register("v")} />
    </Box>
  );
};

export default ExpenseCustomFieldsAddable;
